import {
  ClipboardCopyIcon,
  ClipboardIcon,
  CopyIcon,
  Link2Icon,
  Pencil1Icon,
  StarFilledIcon,
  StarIcon,
  TrashIcon,
} from '@radix-ui/react-icons'
import type { ContextMenuAction } from '~/sotano-kit/primitives/tree'
import * as React from 'react'
import ph from 'posthog-js'
import * as va from '@vercel/analytics'
import {
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
} from '~/sotano-kit/primitives/context-menu'
import type { TreeManagerMethods, TreeNode } from '@basehub/utils/tree/index'
import type { Block } from '@basehub/data/replicache/index'
import { toast } from 'sonner'
import { getBlockTitleForDisplay } from '~/lib/get-block-title-for-display'
import { getId } from '@basehub/utils/id/index'
import { blockIsFavorite } from './fav-utils'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { isComponentBlock, isMediaBlock } from '@basehub/data/replicache/index'
import { useMutateWithErrorHandling } from '~/replicache/frontend/use-mutate-with-error-handling'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { useUserRole } from '~/hooks-data/use-user-role'
import { useTabsStore } from '../tabs/store'
import { getAllowedMenuActions } from '../blocks/block-wrapper-actions/permissions'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  copyBlockToClipboard,
  pasteBlocksFromClipboard,
} from '~/hooks-data/copy-paste'
import { useRepoStore } from '../repo-store'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import {
  DeleteBlocksParams,
  DuplicateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useEditorMode } from '../editor-mode-provider'

type ContextMenuActionsProps = {
  blockTreeManager: TreeManagerMethods<Block, NonNullable<unknown>>
  child: TreeNode<Block>
  setIsRenaming: (value: boolean) => void
  selectedKeys: Set<{ label: string; id: string }>
  isSelected: boolean
  canDuplicate: boolean
}

export const ContextMenuActions = ({
  blockTreeManager,
  child,
  setIsRenaming,
  selectedKeys,
  isSelected,
  canDuplicate,
}: ContextMenuActionsProps) => {
  const isBulkEditing = isSelected && selectedKeys.size > 1
  const addTab = useTabsStore((s) => s.addTab)
  const removeTab = useTabsStore((s) => s.removeTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const cutBlocks = useRepoStore((s) => s.cutBlocks)
  const setCutBlocks = useRepoStore((s) => s.setCutBlocks)

  const { data: repoReplicacheData } = useRepoReplicache()
  const replicache = repoReplicacheData?.replicache
  const favoriteBlocks = useUserPreferences((s) => s.favoriteBlocks)
  const userId = useUserPreferences((s) => s.id)
  const editMode = useUserPreferences((s) => s.editMode)
  const editorMode = useEditorMode()
  const role = useUserRole()

  const isFavorite = React.useMemo(() => {
    if (!favoriteBlocks) return false
    if (isBulkEditing) {
      // all must be favorites
      return Array.from(selectedKeys).every(({ id }) => {
        const node = blockTreeManager.getNode(id)
        return node && blockIsFavorite({ block: node.value, favoriteBlocks })
      })
    } else {
      return blockIsFavorite({ block: child.value, favoriteBlocks })
    }
  }, [
    blockTreeManager,
    child.value,
    isBulkEditing,
    selectedKeys,
    favoriteBlocks,
  ])

  const displayTitle = React.useMemo(() => {
    return getBlockTitleForDisplay(child.value.title)
  }, [child.value.title])

  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const { mutateAsync } = useMutateWithErrorHandling(
    async (
      mutate,
      {
        rootId,
        blockIds,
      }: {
        rootId: string
        blockIds: string[]
      }
    ) => {
      const params: DeleteBlocksParams = {
        rootId,
        blockIds,
        idSeed: getId(),
        isoNow: new Date().toISOString(),
      }
      const result = await mutate.deleteBlocks(params)
      if (result.undo) {
        undoManager.current?.push({
          redo: [{ op: 'deleteBlocks', args: params }],
          undo: [
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
            {
              op: 'focus',
              args: {
                preferredTabKey: activeTabKey,
                focus: {
                  type: 'block:document-title',
                  blockId: idToSourceBlockId(child.value),
                },
              },
            },
          ],
        })
      }
    }
  )

  const {
    onCopyLink,
    onCopyBlock,
    onPasteBlock,
    onDelete,
    onFavorite,
    onRename,
    onUnfavorite,
    onDuplicate,
  } = React.useMemo(() => {
    const onFavorite = async () => {
      if (!replicache) throw new Error('replicache not found')
      if (!userId) throw new Error('userPreferences not found')
      await replicache.mutate.makeBlocksFavorite({
        blockIds: isBulkEditing
          ? Array.from(selectedKeys).map(({ id }) =>
              idToSourceBlockId(id, blockTreeManager)
            )
          : [idToSourceBlockId(child.key, blockTreeManager)],
        userId,
      })
      const successMessage = `Added ${
        isBulkEditing ? `${selectedKeys.size} blocks` : `"${displayTitle}"`
      } to favorites`
      toast.success(successMessage)
    }

    const onUnfavorite = async () => {
      if (!replicache) throw new Error('replicache not found')
      if (!userId) throw new Error('userPreferences not found')
      await replicache.mutate.makeBlocksUnFavorite({
        blockIds: isBulkEditing
          ? Array.from(selectedKeys).map(({ id }) =>
              idToSourceBlockId(id, blockTreeManager)
            )
          : [idToSourceBlockId(child.key, blockTreeManager)],
        userId,
      })
      const successMessage = `Removed ${
        isBulkEditing ? `${selectedKeys.size} blocks` : `"${displayTitle}"`
      } from favorites`
      toast.success(successMessage)
    }

    const onCopyLink = async () => {
      const url = new URL(document.URL)
      const path = url.pathname.split('/')
      path.pop() // remove the last
      url.pathname = path.join('/')
      await navigator.clipboard.writeText(
        url.origin +
          url.pathname +
          '/' +
          idToSourceBlockId(child.key, blockTreeManager)
      )
      toast.success('Link copied to clipboard', {
        icon: <ClipboardCopyIcon width={16} height={16} />,
      })
    }

    const onRename = () => {
      setIsRenaming(true)
    }

    const onDelete = async () => {
      if (!replicache) throw new Error('replicache not found')

      const blockIds = (
        isBulkEditing
          ? Array.from(selectedKeys).map(({ id }) => id)
          : [child.key]
      ).map((id) => blockTreeManager.getNode(id, { required: true }).value.id)

      await mutateAsync({ rootId: blockTreeManager.root.key, blockIds })
      removeTab(blockIds)

      const successMessage = `Deleted ${
        isBulkEditing ? `${selectedKeys.size} blocks` : `"${displayTitle}"`
      }`
      toast.success(successMessage, {
        icon: <TrashIcon width={16} height={16} />,
      })
    }

    const onDuplicate = async () => {
      // If an image is being uploaded, wait for it to finish before duplicating.
      if (
        isMediaBlock(child.value) &&
        child.value.value.status === 'uploading'
      ) {
        return toast.error(
          'Please wait for the file to finish uploading before duplicating the block.'
        )
      }

      const path = blockTreeManager.getPath(child.key, {
        format: 'string',
        required: true,
      })

      const id = getId()
      const params: DuplicateBlockParams = {
        duplicatedBlockId: child.value.id,
        newBlockId: id,
        path,
        position: {
          type: 'after',
          targetId: child.value.id,
        },
        isoNow: new Date().toISOString(),
        idSeed: getId(),
        duplicatedComponentStrategy: isComponentBlock(child.value)
          ? 'create-instance'
          : 'default',
      }
      await replicache?.mutate
        .duplicateBlock(params)
        .then((result) => {
          va.track('Block created', {
            source: 'Context menu (block duplicated)',
            blockType: child.value.type,
          })
          ph.capture('Block created', {
            source: 'Context menu (block duplicated)',
            blockType: child.value.type,
          })
          addTab({ key: id, partiallyOpen: true })
          setFocus({ type: 'block:name', blockId: id }) // focus shuold be at the start of this new block though.
          if (result?.undo) {
            undoManager.current?.push({
              redo: [{ op: 'duplicateBlock', args: params }],
              undo: [
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
                {
                  op: 'focus',
                  args: {
                    preferredTabKey: activeTabKey,
                    focus: {
                      type: 'block:document-title',
                      blockId: idToSourceBlockId(child.value),
                    },
                  },
                },
              ],
            })
          }
        })
        .catch((err) => {
          console.error(err)
          if (err instanceof MutatorUserError) {
            toast.error(err.reason)
          } else {
            console.error(err)
            throw err
          }
        })
    }

    const onCopyBlock = async () => {
      await copyBlockToClipboard({
        blockTreeManager,
        blockId: child.key,
        onSuccess: () => {
          toast.success(
            `"${child.value.title ?? 'Untitled'}" copied to clipboard`,
            {
              icon: <ClipboardCopyIcon width={16} height={16} />,
            }
          )
        },
        onError: (err) => {
          console.error(err)
          toast.error('Failed to copy block to clipboard')
        },
      })
    }

    const onPasteBlock = async () => {
      if (!replicache) throw new Error('replicache not found')
      const pasteResult = await pasteBlocksFromClipboard({
        repoId: repoReplicacheData.repoId,
        blockTreeManager,
        selectedBlock: child.value,
        addTab,
        destinationPath: blockTreeManager.getPath(child.key, {
          required: true,
          format: 'string',
        }),
        editModeIsEnabled: editMode,
        replicache,
        repoStore: { setCutBlocks, cutBlocks },
        shiftKeyPressed: false,
      })
      if (pasteResult?.newBlock && pasteResult?.undo) {
        const focusOp = {
          op: 'focus',
          args: {
            focus: {
              blockId: idToSourceBlockId(pasteResult.newBlock),
              type: 'block:sidebar',
            },
            preferredTabKey: activeTabKey,
          },
        } as const
        undoManager.current?.push({
          redo: [pasteResult.redo, focusOp],
          undo: [
            focusOp,
            {
              op: 'blockBatchMutations',
              args: { mutations: pasteResult.undo },
            },
          ],
        })
      }
    }

    return {
      isFavorite,
      onCopyLink,
      onCopyBlock,
      onPasteBlock,
      onDelete,
      onFavorite,
      onRename,
      onUnfavorite,
      onDuplicate,
    }
  }, [
    addTab,
    blockTreeManager,
    child.key,
    child.value,
    cutBlocks,
    displayTitle,
    editMode,
    isBulkEditing,
    isFavorite,
    mutateAsync,
    removeTab,
    replicache,
    repoReplicacheData?.repoId,
    selectedKeys,
    setCutBlocks,
    setFocus,
    setIsRenaming,
    userId,
    undoManager,
    activeTabKey,
  ])

  const actions: ContextMenuAction[] = React.useMemo(() => {
    const parent = child.parentKey
      ? blockTreeManager.getNode(child.parentKey)?.value
      : undefined

    const path = blockTreeManager.getPath(child.key, {
      required: true,
      format: 'string',
    })

    const permissions = getAllowedMenuActions({
      block: child.value,
      editorMode,
      parentBlock: parent,
      userRole: role,
      path,
      treeManager: blockTreeManager,
    })

    const result: ContextMenuAction[] = []
    if (permissions.favorite) {
      result.push(
        {
          type: 'action',
          label: isFavorite ? 'Remove from favorites' : 'Add to favorites',
          icon: isFavorite ? (
            <StarFilledIcon width={16} height={16} />
          ) : (
            <StarIcon width={16} height={16} />
          ),
          onSelect: () => {
            if (isFavorite) onUnfavorite()
            else onFavorite()
          },
        },
        { type: 'separator' }
      )
    }
    if (permissions.copy) {
      result.push(
        {
          type: 'action',
          label: 'Copy link',
          icon: <Link2Icon width={16} height={16} />,
          disabled: isBulkEditing,
          onSelect: () => {
            onCopyLink()
          },
        },
        {
          type: 'action',
          label: 'Copy block',
          icon: <ClipboardCopyIcon width={16} height={16} />,
          disabled: isBulkEditing,
          onSelect: onCopyBlock,
        }
      )

      if (permissions.paste) {
        result.push({
          type: 'action',
          label: 'Paste block',
          icon: <ClipboardIcon width={16} height={16} />,
          disabled: isBulkEditing,
          onSelect: onPasteBlock,
        })
      }

      result.push({
        type: 'separator',
      })
    }
    if (permissions.rename) {
      result.push({
        type: 'action',
        label: 'Rename',
        icon: <Pencil1Icon width={16} height={16} />,
        disabled: isBulkEditing,
        onSelect: () => {
          onRename()
        },
      })
    }
    if (permissions.duplicate && canDuplicate) {
      result.push({
        type: 'action',
        label: 'Duplicate',
        icon: <CopyIcon width={16} height={16} />,
        disabled: isBulkEditing,
        onSelect: () => {
          onDuplicate()
        },
      })
    }
    if (permissions.delete) {
      result.push(
        { type: 'separator' },
        {
          type: 'action',
          label: 'Delete',
          icon: <TrashIcon width={16} height={16} />,
          onSelect: () => {
            onDelete()
          },
        }
      )
    }

    if (result.at(-1)?.type === 'separator') {
      result.pop() // clean up trailing separator
    }

    return result
  }, [
    blockTreeManager,
    canDuplicate,
    child.key,
    child.parentKey,
    child.value,
    editorMode,
    isBulkEditing,
    isFavorite,
    onCopyBlock,
    onCopyLink,
    onDelete,
    onDuplicate,
    onFavorite,
    onPasteBlock,
    onRename,
    onUnfavorite,
    role,
  ])

  return (
    <>
      {isBulkEditing && (
        <ContextMenuLabel>
          <span className="text-dark-muted">Bulk editing</span>{' '}
          {selectedKeys.size} blocks
        </ContextMenuLabel>
      )}
      {actions.map((action, i) => {
        if (action.type === 'separator') {
          return <ContextMenuSeparator key={i} />
        }
        return (
          <ContextMenuItem
            key={i}
            onSelect={action.onSelect}
            disabled={action.disabled}
          >
            <span className="mr-2">{action.icon}</span>
            {action.label}
          </ContextMenuItem>
        )
      })}
    </>
  )
}
